import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Settings, Check, X, Shield, BarChart3, Target, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import API from '@/lib/api';
import cookieConsent from '@/lib/cookieConsent';
import { useSiteSettings } from '@/context/SiteSettingsContext';
import { COOKIE_BANNER_DEFAULTS } from '@/lib/cookieBannerDefaults';

const ICONS = { essential: Shield, analytics: BarChart3, functional: Sparkles, marketing: Target };

export default function CookieBanner() {
  const { cookieBanner } = useSiteSettings();
  const cb = { ...COOKIE_BANNER_DEFAULTS, ...(cookieBanner || {}) };
  const categories = (Array.isArray(cb.categories) && cb.categories.length ? cb.categories : COOKIE_BANNER_DEFAULTS.categories)
    .filter(c => ICONS[c.id]).map(c => ({ ...c, icon: ICONS[c.id] }));
  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [policyVersion, setPolicyVersion] = useState(null);
  const [choices, setChoices] = useState({
    functional: false, analytics: false, marketing: false,
  });

  const fetchVersion = useCallback(async () => {
    try {
      const { data } = await API.get('/cookie-consent/version');
      setPolicyVersion(data.policy_version);
      // If user has chosen but with older version, re-prompt
      const current = cookieConsent.get();
      if (cookieConsent.hasMadeChoice() && current.policy_version && current.policy_version < data.policy_version) {
        setVisible(true);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchVersion();
    if (!cookieConsent.hasMadeChoice()) {
      setVisible(true);
    }
    const reopen = () => { setVisible(true); setModalOpen(false); };
    window.addEventListener('reopen-cookie-banner', reopen);
    return () => window.removeEventListener('reopen-cookie-banner', reopen);
  }, [fetchVersion]);

  const logConsent = async (state, action) => {
    try {
      await API.post('/cookie-consent', {
        functional: state.functional,
        analytics: state.analytics,
        marketing: state.marketing,
        action,
        policy_version: policyVersion,
        client_id: cookieConsent.get().client_id,
      }).then(res => {
        // Update local state with server-returned client_id (idempotent)
        cookieConsent.setAll({
          ...state,
          action,
          policy_version: res.data?.policy_version || policyVersion,
          client_id: res.data?.client_id,
        });
      });
    } catch {
      // Even if the log fails, persist client-side
      cookieConsent.setAll({ ...state, action, policy_version: policyVersion });
    }
  };

  const acceptAll = async () => {
    await logConsent({ functional: true, analytics: true, marketing: true }, 'accept_all');
    setVisible(false);
    setModalOpen(false);
  };

  const rejectAll = async () => {
    await logConsent({ functional: false, analytics: false, marketing: false }, 'reject_all');
    setVisible(false);
    setModalOpen(false);
  };

  const saveCustom = async () => {
    await logConsent(choices, 'save_custom');
    setVisible(false);
    setModalOpen(false);
  };

  const openCustomize = () => {
    // Pre-fill with current state
    const current = cookieConsent.get();
    setChoices({
      functional: !!current.functional,
      analytics: !!current.analytics,
      marketing: !!current.marketing,
    });
    setModalOpen(true);
  };

  if (!visible) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 pointer-events-none" data-testid="cookie-banner">
        <div className="max-w-3xl mx-auto bg-stone-text text-white rounded-2xl shadow-2xl border border-white/10 p-5 md:p-6 pointer-events-auto">
          <div className="flex items-start gap-4">
            <Cookie className="h-6 w-6 text-brick shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="font-serif text-base font-medium mb-1" data-testid="cookie-banner-title">{cb.title}</h3>
              <p className="font-sans text-sm text-white/70 leading-relaxed" data-testid="cookie-banner-text">
                {cb.text}{' '}
                <Link to={cb.link_url || '/politique-cookies'} className="text-brick underline hover:text-brick/80" data-testid="cookie-policy-link">{cb.link_label}</Link>
              </p>
              <div className="flex flex-wrap gap-3 mt-4">
                <Button onClick={acceptAll} className="bg-brick text-white rounded-full px-6 text-sm font-sans hover:bg-brick/90" data-testid="cookie-accept">
                  {cb.accept_label}
                </Button>
                <Button onClick={rejectAll} className="bg-stone-text text-white rounded-full px-6 text-sm font-sans border border-white/30 hover:bg-white/10" data-testid="cookie-reject-all">
                  {cb.reject_label}
                </Button>
                <Button onClick={openCustomize} variant="ghost" className="rounded-full px-5 text-sm font-sans text-white hover:bg-white/10" data-testid="cookie-customize">
                  <Settings className="h-3.5 w-3.5 mr-1.5" /> {cb.customize_label}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cookie-customize-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              <Cookie className="h-5 w-5 text-brick" /> {cb.modal_title}
            </DialogTitle>
            <DialogDescription className="font-sans text-sm">
              {cb.modal_description}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {categories.map(cat => {
              const Icon = cat.icon;
              const isEssential = cat.id === 'essential';
              const checked = isEssential ? true : choices[cat.id];
              return (
                <div key={cat.id}
                  className={`border rounded-lg p-4 ${checked ? 'border-brick/30 bg-brick/5' : 'border-clay bg-white'}`}
                  data-testid={`cookie-category-${cat.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${checked ? 'text-brick' : 'text-muted-foreground'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-serif text-base font-medium">{cat.title}</h4>
                          {isEssential && <span className="text-[10px] font-sans uppercase tracking-wider bg-stone-700 text-white px-2 py-0.5 rounded-full">Toujours actif</span>}
                        </div>
                        <p className="font-sans text-xs text-muted-foreground mt-1 leading-relaxed">{cat.description}</p>
                        <p className="font-sans text-[11px] text-muted-foreground/80 mt-2">
                          <span className="uppercase tracking-wider">Cookies :</span> {Array.isArray(cat.cookies) ? cat.cookies.join(', ') : cat.cookies}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={checked}
                      disabled={isEssential}
                      onCheckedChange={(v) => !isEssential && setChoices(c => ({ ...c, [cat.id]: v }))}
                      data-testid={`cookie-toggle-${cat.id}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <DialogFooter className="flex flex-wrap gap-2 sm:justify-between sm:items-center">
            <div className="flex gap-2">
              <Button variant="ghost" onClick={rejectAll} className="rounded-full font-sans text-sm" data-testid="cookie-modal-reject">
                <X className="h-3.5 w-3.5 mr-1" /> {cb.reject_label}
              </Button>
              <Button variant="ghost" onClick={acceptAll} className="rounded-full font-sans text-sm" data-testid="cookie-modal-accept">
                <Check className="h-3.5 w-3.5 mr-1" /> {cb.accept_label}
              </Button>
            </div>
            <Button onClick={saveCustom} className="bg-brick text-white rounded-full font-sans hover:bg-brick/90" data-testid="cookie-modal-save">
              {cb.save_label}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
